import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Tag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MAX_VISIBLE = 50;

function SearchableField({ label, testId, options, value, onChange, placeholder, displayFn }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const display = displayFn || ((v) => v);
  const filtered = options.filter((o) => display(o).toLowerCase().includes(search.toLowerCase()));
  const shown = filtered.slice(0, MAX_VISIBLE);

  if (value) {
    return (
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>
        <div
          data-testid={testId}
          className="border border-stone-200 rounded-md px-3 py-2 h-10 flex items-center justify-between bg-white cursor-pointer text-sm"
          onClick={() => { onChange(""); setSearch(""); setOpen(true); }}
        >
          <span className="truncate">{display(value)}</span>
          <span className="text-stone-400 text-xs hover:text-red-500 ml-2 shrink-0">change</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>
      <input
        data-testid={testId}
        className="w-full border border-stone-200 rounded-md px-3 py-2 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#C2410C] bg-white"
        placeholder={placeholder}
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {shown.length === 0 ? (
            <div className="px-3 py-3 text-sm text-stone-400 text-center">
              {search ? "No matches" : "Loading..."}
            </div>
          ) : (
            <>
              {shown.map((o) => (
                <div
                  key={o}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 hover:text-[#C2410C] border-b border-stone-50 last:border-0"
                  onClick={() => { onChange(o); setSearch(""); setOpen(false); }}
                >
                  {display(o)}
                </div>
              ))}
              {filtered.length > MAX_VISIBLE && (
                <div className="px-3 py-2 text-xs text-stone-400 text-center bg-stone-50">
                  Showing {MAX_VISIBLE} of {filtered.length.toLocaleString()} — type to narrow down
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Inline cascading barcode picker — Category4 → Design → Shade → Size → Barcode.
 * Posts /saved-designs/{designId}/tag-barcode on submit.
 * Used on every calculator page right after a successful save.
 */
export default function TagBarcodePicker({ designId, onTagged, onSkip }) {
  const [cat4, setCat4] = useState("");
  const [designName, setDesignName] = useState("");
  const [shade, setShade] = useState("");
  const [size, setSize] = useState("");
  const [barcode, setBarcode] = useState("");
  const [cascading, setCascading] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchCascading({}); /* eslint-disable-next-line */ }, []);

  const fetchCascading = async (params) => {
    try {
      const query = new URLSearchParams();
      if (params.category4) query.set("category4", params.category4);
      if (params.design_name) query.set("design_name", params.design_name);
      if (params.shade) query.set("shade", params.shade);
      if (params.size) query.set("size", params.size);
      const res = await api.get(`/item-master/cascading?${query.toString()}`);
      const data = res.data;
      setCascading((prev) => ({ ...prev, ...data }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onCat4Change = (v) => { setCat4(v); setDesignName(""); setShade(""); setSize(""); setBarcode(""); setCascading((p) => ({ category4_options: p.category4_options })); if (v) fetchCascading({ category4: v }); };
  const onDesignNameChange = (v) => { setDesignName(v); setShade(""); setSize(""); setBarcode(""); setCascading((p) => ({ category4_options: p.category4_options, design_name_options: p.design_name_options })); if (v) fetchCascading({ category4: cat4, design_name: v }); };
  const onShadeChange = (v) => { setShade(v); setSize(""); setBarcode(""); setCascading((p) => ({ ...p, size_options: undefined, items: undefined })); if (v) fetchCascading({ category4: cat4, design_name: designName, shade: v }); };
  const onSizeChange = (v) => { setSize(v); setBarcode(""); setCascading((p) => ({ ...p, items: undefined })); if (v) fetchCascading({ category4: cat4, design_name: designName, shade: shade, size: v }); };

  const submitTag = async () => {
    if (!barcode || !designId) return;
    setLoading(true);
    try {
      const res = await api.put(`/saved-designs/${designId}/tag-barcode`, { barcode });
      const trimsTotal = parseFloat(res.data?.trims_total || 0);
      if (trimsTotal > 0) {
        toast.success(`Barcode tagged — trims auto-filled: ₹${trimsTotal.toFixed(2)}`);
      } else {
        toast.success("Barcode tagged");
      }
      if (onTagged) onTagged({ trimsTotal, barcode, response: res.data });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="inline-tag-picker">
      <div className="text-xs text-stone-500 -mb-1">Optionally tag a barcode to this design now. Skip to continue with a fresh entry.</div>
      <SearchableField label="Category 4" testId="inline-tag-cat4" options={cascading.category4_options || []} value={cat4} onChange={onCat4Change} placeholder="Search Category 4..." />
      {cat4 && <SearchableField label="Design Name" testId="inline-tag-design-name" options={cascading.design_name_options || []} value={designName} onChange={onDesignNameChange} placeholder="Type to search..." />}
      {designName && <SearchableField label="Shade" testId="inline-tag-shade" options={cascading.shade_options || []} value={shade} onChange={onShadeChange} placeholder="Search shade..." />}
      {shade && <SearchableField label="Size" testId="inline-tag-size" options={cascading.size_options || []} value={size} onChange={onSizeChange} placeholder="Search size..." />}
      {size && cascading.items && (
        <SearchableField
          label="Description / Barcode"
          testId="inline-tag-barcode"
          options={(cascading.items || []).map((item) => item.barcode)}
          displayFn={(bc) => {
            const item = (cascading.items || []).find((i) => i.barcode === bc);
            return item ? `${item.description || bc} — WSP: ${item.wsp} / MRP: ${item.mrp}` : bc;
          }}
          value={barcode}
          onChange={setBarcode}
          placeholder="Search barcode..."
        />
      )}
      <div className="flex gap-2 pt-2">
        <Button variant="outline" className="flex-1" onClick={onSkip} data-testid="inline-tag-skip">Skip</Button>
        <Button data-testid="inline-tag-submit" className="flex-1 bg-[#C2410C] hover:bg-[#9A3412] text-white" disabled={!barcode || loading} onClick={submitTag}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Tag className="h-4 w-4 mr-2" />}
          Tag Barcode
        </Button>
      </div>
    </div>
  );
}
